import { useMemo } from 'react';
import { Stack } from '@mui/system';
import {
  GeneralLedgerUploadData,
  generalLedgerUploadDataSchema,
} from '../../../schemas/GeneralLedgerUploadData.schema';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { withOverrides } from '../../../util/ag-grid-utils';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { AgTable } from '../../../components/AgTable/AgTable';

export function GeneralLedgerUploadsGrid({ data }: { data: GeneralLedgerUploadData[] }) {
  const columnDefs = useGeneralLedgerUploadsColDefs();
  return (
    <FadeInGridWrapper>
      <Stack height='80vh' mt='1rem'>
        <AgTable
          columnDefs={columnDefs}
          rowData={data}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='never'
        />
      </Stack>
    </FadeInGridWrapper>
  );
}

function useGeneralLedgerUploadsColDefs() {
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = generalLedgerUploadDataSchema();
    // Surface `sourceObject` inline in parens next to the source label (e.g.
    // "SS&C / other FAs (generalLedger)") rather than as its own column. Carta
    // rows predate Atlas and have no sourceObject — render the bare label.
    // `filterValueGetter` runs per row (receives `data`) and bakes the
    // formatted string into each row's filter value, so ag-grid's Set Filter
    // derives one entry per distinct (source, sourceObject) pair. The Set
    // Filter's `valueFormatter` cannot do this — it's invoked with synthetic
    // params where `data` is undefined.
    const formatSource = (src: string | undefined, obj: string | undefined) => {
      if (src && obj && src !== 'Carta') {
        return `${src} (${obj})`;
      }
      return src ?? '';
    };
    return withOverrides(schemaToGrid(schema), {
      source: {
        valueFormatter: (params) =>
          formatSource(
            params.value as string | undefined,
            (params.data as GeneralLedgerUploadData | undefined)?.sourceObject
          ),
        filterValueGetter: (params) =>
          formatSource(
            (params.data as GeneralLedgerUploadData | undefined)?.source,
            (params.data as GeneralLedgerUploadData | undefined)?.sourceObject
          ),
      },
    });
  }, [schemaToGrid]);
}

const defaultColDef = {
  flex: 1,
};
